import { useEffect, useMemo, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useGetSpiderChartQuery } from 'Deer/pages/MyData/Overview/cards/FeedbackOverview/dataHooks';
import { type CompetencyDetails } from 'Deer/pages/MyData/Overview/types/FeedbackMyOverview';
import { useToast } from 'components/hooks/useToast';

import { BottomDrawer, DrawerContent, DrawerHeader, primaryDrawerWidth } from 'uiToolkitV1/BottomDrawer';

import { CompetencyFooter } from './CompetencyFooter';
import { CompetencyHeader } from './CompetencyHeader';
import { CompetencyInitiativesList } from './CompetencyInitiativesList';
import { CompetencyLoading } from './CompetencyLoading';

type CompetencyReviewActionPlanBottomSheetProps = {
  onClose: () => void;
  employeeId: string;
  reviewCycleId: string;
  actionPlanId: number;
  toIds: { uuid: string; ownerType: string; metadata: { displayName: string }; profile: { profilePhoto: string } }[];
  onAddInitiative?: () => void;
};

export const CompetencyReviewActionPlanBottomSheet = ({
  onClose,
  employeeId,
  reviewCycleId,
  actionPlanId,
  toIds,
  onAddInitiative,
}: CompetencyReviewActionPlanBottomSheetProps) => {
  const [selectedCompetency, setSelectedCompetency] = useState<CompetencyDetails | null>(null);

  const toast = useToast();
  const {
    data: competencySpiderDetails,
    isLoading: isCompetencySpiderDetailsLoading,
    isError: isCompetencySpiderDetailsError,
  } = useGetSpiderChartQuery();

  const competencyDetails = useMemo(() => {
    if (competencySpiderDetails) return competencySpiderDetails.competencyDetails;
    return null;
  }, [competencySpiderDetails]);

  // setting default selected competency
  useEffect(() => {
    if (competencyDetails) setSelectedCompetency(competencyDetails[0]);
  }, [competencyDetails]);

  if (isCompetencySpiderDetailsError) {
    toast({
      title: 'An unknown error has occurred, please try again',
      status: 'error',
    });
    return null;
  }

  return (
    <BottomDrawer isOpen onClose={onClose}>
      <DrawerContent width={`${primaryDrawerWidth}px`}>
        <DrawerHeader onClose={onClose}>{undefined}</DrawerHeader>
        <Box h="100%" p="4px" borderRadius="8px" bg="white">
          <Box borderRadius="8px" bg="#CFD7DF" h="100%" width="100%" p="4px">
            <Box bg="white" borderBottom="1px solid #CFD7DF" h="100%" width="100%">
              <Flex flexDirection="column" height="100%" borderRadius="8px 8px 0 0">
                {isCompetencySpiderDetailsLoading && (
                  <>
                    <CompetencyLoading />
                    <CompetencyFooter onClose={onClose} />
                  </>
                )}
                {!isCompetencySpiderDetailsLoading && competencyDetails && selectedCompetency && (
                  <>
                    <CompetencyHeader
                      selectedCompetency={selectedCompetency}
                      setSelectedCompetency={setSelectedCompetency}
                      competencyDetails={competencyDetails}
                    />
                    <CompetencyInitiativesList
                      employeeId={employeeId}
                      competencyId={selectedCompetency.competencyId.toString()}
                      reviewCycleId={reviewCycleId}
                      actionPlanId={actionPlanId}
                      toIds={toIds}
                      onAddInitiative={onAddInitiative}
                    />
                    <CompetencyFooter onClose={onClose} />
                  </>
                )}
              </Flex>
            </Box>
          </Box>
        </Box>
      </DrawerContent>
    </BottomDrawer>
  );
};
